import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { useSnackbar } from 'react-simple-snackbar'
import { fetchCorporateDashboard, deleteCorporateCampaign } from '../../api/index'
import { useCorporateGuard } from '../../hooks/useRoleGuard'
import Spinner from '../Spinner/Spinner'
import ConfirmModal from '../ConfirmModal/ConfirmModal'
import styles from '../Dashboard/Dashboard.module.css'

const MONTHS = [
  '', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
]

const formatPeriod = (period) => {
  if (!period) return '—'
  const [year, month] = period.split('-')
  return `${MONTHS[Number(month)]} ${year}`
}

const CorporateDashboard = () => {
  const history = useHistory()
  const [openSnackbar] = useSnackbar()
  const openSnackbarRef = useRef(openSnackbar)
  openSnackbarRef.current = openSnackbar
  useCorporateGuard()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [pageLoading, setPageLoading] = useState(false)
  const [page, setPage] = useState(1)
  const now = new Date()
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1)
  const [selectedYear, setSelectedYear] = useState(now.getFullYear())
  const [errorsOnly, setErrorsOnly] = useState(false)
  const [selectedIds, setSelectedIds] = useState([])
  const [deleting, setDeleting] = useState(false)
  const [confirmModal, setConfirmModal] = useState(null)
  const hasLoadedOnce = useRef(false)
  const limit = 5

  const load = useCallback(async () => {
    if (hasLoadedOnce.current) setPageLoading(true)
    else setLoading(true)
    try {
      const { data: result } = await fetchCorporateDashboard({
        page,
        limit,
        month: selectedMonth,
        year: selectedYear,
        errorsOnly,
      })
      setData(result)
      hasLoadedOnce.current = true
    } catch (error) {
      openSnackbarRef.current(error?.response?.data?.message || 'Erreur chargement')
    } finally {
      setLoading(false)
      setPageLoading(false)
    }
  }, [page, selectedMonth, selectedYear, errorsOnly])

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, selectedMonth, selectedYear, errorsOnly])

  useEffect(() => {
    setSelectedIds([])
  }, [page, errorsOnly])

  useEffect(() => {
    setPage(1)
  }, [errorsOnly])

  if (loading && !data) {
    return <div className={styles.loading}><Spinner /></div>
  }

  const {
    stats,
    recentCampaigns = [],
    selectedCampaign,
    selectedPeriod,
    availableYears = [],
    pagination,
  } = data || {}

  const emailStats = stats?.periodEmailStats || { sent: 0, pending: 0, failed: 0 }
  const emailTotal = emailStats.sent + emailStats.pending + emailStats.failed
  const emailProgress = emailTotal > 0 ? Math.round((emailStats.sent / emailTotal) * 100) : 0

  const currentMonth = now.getMonth() + 1
  const currentYear = now.getFullYear()
  const isCurrentPeriod = selectedMonth === currentMonth && selectedYear === currentYear
  const years = availableYears.length > 0 ? availableYears : [currentYear]
  const activePeriod = selectedPeriod || `${selectedYear}-${String(selectedMonth).padStart(2, '0')}`

  const pageIds = recentCampaigns.map((c) => c.id)
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id))

  const goToCurrentMonth = () => {
    setSelectedMonth(currentMonth)
    setSelectedYear(currentYear)
  }

  const selectPeriodFromCampaign = (period) => {
    const [year, month] = period.split('-')
    setSelectedYear(Number(year))
    setSelectedMonth(Number(month))
  }

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    )
  }

  const toggleSelectAllOnPage = () => {
    if (allPageSelected) {
      setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)))
    } else {
      setSelectedIds((prev) => [...new Set([...prev, ...pageIds])])
    }
  }

  const executeDeleteCampaigns = async (ids) => {
    if (!ids.length) return
    setDeleting(true)
    try {
      await Promise.all(ids.map((id) => deleteCorporateCampaign(id)))
      openSnackbarRef.current(`${ids.length} campagne(s) supprimée(s)`)
      setSelectedIds((prev) => prev.filter((id) => !ids.includes(id)))
      await load()
    } catch (error) {
      openSnackbarRef.current(error?.response?.data?.message || 'Erreur suppression')
    } finally {
      setDeleting(false)
    }
  }

  const requestDeleteCampaigns = (ids) => {
    if (!ids.length) return
    const label = ids.length === 1 ? 'cette campagne' : `${ids.length} campagnes`
    setConfirmModal({
      ids,
      variant: 'warning',
      title: 'Supprimer la campagne',
      message: `Supprimer ${label} ?`,
      detail: 'Tous les PDF et documents associés seront définitivement supprimés.',
      confirmLabel: 'Supprimer',
    })
  }

  const handleConfirmModal = () => {
    if (!confirmModal) return
    const { ids } = confirmModal
    setConfirmModal(null)
    executeDeleteCampaigns(ids)
  }

  return (
    <div className={styles.page}>
      <div className={styles.topBar}>
        <div>
          <h1>Dashboard Corporate</h1>
          <p>Conventions clients — envoi PDF Sage X3</p>
        </div>
        <button type="button" className={styles.ctaBtn} onClick={() => history.push('/corporate/import')}>
          + Nouvel envoi PDF
        </button>
      </div>

      <div className={styles.filterBar}>
        <div className={styles.filterGroup}>
          <label htmlFor="corp-dash-month">Mois</label>
          <select
            id="corp-dash-month"
            className={styles.filterSelect}
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(Number(e.target.value))}
          >
            {MONTHS.slice(1).map((label, i) => (
              <option key={label} value={i + 1}>{label}</option>
            ))}
          </select>
        </div>
        <div className={styles.filterGroup}>
          <label htmlFor="corp-dash-year">Année</label>
          <select
            id="corp-dash-year"
            className={styles.filterSelect}
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
          >
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        {!isCurrentPeriod && (
          <button type="button" className={styles.resetBtn} onClick={goToCurrentMonth}>
            Revenir au mois en cours
          </button>
        )}
      </div>

      <div className={styles.periodBanner}>
        <div>
          <span className={styles.periodLabel}>
            {isCurrentPeriod ? 'Période en cours' : 'Période sélectionnée'}
          </span>
          <strong>{formatPeriod(activePeriod)}</strong>
        </div>
        {selectedCampaign && (
          <div className={styles.periodStatus}>
            <span className={styles.badgeOk}>Campagne active</span>
            <span>{emailProgress}% emails envoyés</span>
            <button type="button" className={styles.linkBtn} onClick={() => history.push(`/corporate/campagnes/${selectedCampaign._id}`)}>
              Voir détail →
            </button>
          </div>
        )}
      </div>

      <div className={styles.kpiGrid}>
        <div className={`${styles.kpiCard} ${styles.kpiPrimary}`}>
          <span className={styles.kpiLabel}>Clients convention</span>
          <span className={styles.kpiValue}>{stats?.totalClients || 0}</span>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>PDF période</span>
          <span className={styles.kpiValue}>{stats?.periodDocuments || 0}</span>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>Emails envoyés</span>
          <span className={styles.kpiValue}>{emailStats.sent} / {emailTotal || 0}</span>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>Campagnes totales</span>
          <span className={styles.kpiValue}>{stats?.totalCampaigns || 0}</span>
        </div>
      </div>

      <div className={styles.panel}>
        <div className={styles.panelHeader}>
          <div>
            <h2>Campagnes récentes</h2>
            <p className={styles.historyHint}>
              Ouvrir le détail ou supprimer une ou plusieurs campagnes.
            </p>
          </div>
          <div className={styles.panelActions}>
            {selectedIds.length > 0 && (
              <button
                type="button"
                className={styles.deleteBtn}
                disabled={deleting || pageLoading}
                onClick={() => requestDeleteCampaigns(selectedIds)}
              >
                {deleting ? 'Suppression...' : `Supprimer (${selectedIds.length})`}
              </button>
            )}
            {pagination?.total > 0 && (
              <span className={styles.pageInfo}>
                {pagination.total} campagne(s) · page {pagination.page}/{pagination.totalPages}
              </span>
            )}
          </div>
        </div>

        <div className={styles.filterBar} style={{ marginBottom: '1rem', padding: '0.75rem 1rem' }}>
          <label className={styles.filterGroup} style={{ flexDirection: 'row', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={errorsOnly}
              onChange={(e) => setErrorsOnly(e.target.checked)}
              aria-label="Afficher uniquement les campagnes avec erreurs"
            />
            <span>Campagnes avec erreurs uniquement</span>
          </label>
          {errorsOnly && (
            <button
              type="button"
              className={styles.resetBtn}
              onClick={() => setErrorsOnly(false)}
            >
              Réinitialiser le filtre
            </button>
          )}
        </div>

        {recentCampaigns.length === 0 ? (
          <p className={styles.empty}>
            {errorsOnly
              ? 'Aucune campagne avec erreurs.'
              : 'Aucune campagne Corporate'}
          </p>
        ) : (
          <div className={pageLoading ? styles.tableLoading : undefined}>
            <div className="table-scroll-wrap">
            <table className={`${styles.table} responsive-table`}>
              <thead>
                <tr>
                  <th className={styles.checkCol}>
                    <input
                      type="checkbox"
                      checked={allPageSelected}
                      onChange={toggleSelectAllOnPage}
                      aria-label="Sélectionner toutes les campagnes de la page"
                    />
                  </th>
                  <th>Période</th>
                  <th>PDF</th>
                  <th>Erreurs</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {recentCampaigns.map((c) => (
                  <tr
                    key={c.id}
                    className={c.period === activePeriod ? styles.rowActive : undefined}
                  >
                    <td className={styles.checkCol} data-label="Sélection">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(c.id)}
                        onChange={() => toggleSelect(c.id)}
                        aria-label={`Sélectionner ${formatPeriod(c.period)}`}
                      />
                    </td>
                    <td data-label="Période">
                      <button
                        type="button"
                        className={styles.periodLink}
                        onClick={() => selectPeriodFromCampaign(c.period)}
                      >
                        {formatPeriod(c.period)}
                      </button>
                    </td>
                    <td data-label="PDF">{c.successCount}</td>
                    <td data-label="Erreurs">
                      {c.errorCount > 0 ? (
                        <span className={styles.badgeWarn}>{c.errorCount}</span>
                      ) : (
                        c.errorCount
                      )}
                    </td>
                    <td data-label="Actions">
                      <div className={`${styles.actionCell} action-cell-inner`}>
                        <button type="button" className={styles.tableBtn} onClick={() => history.push(`/corporate/campagnes/${c.id}`)}>
                          Ouvrir
                        </button>
                        <button
                          type="button"
                          className={styles.deleteBtnSmall}
                          disabled={deleting}
                          onClick={() => requestDeleteCampaigns([c.id])}
                        >
                          Supprimer
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>

            {pagination?.totalPages > 1 && (
              <div className={styles.pagination}>
                <button
                  type="button"
                  className={styles.pageBtn}
                  disabled={page <= 1 || pageLoading}
                  onClick={() => setPage((p) => p - 1)}
                >
                  ← Précédent
                </button>
                <span className={styles.pageLabel}>
                  Page {pagination.page} sur {pagination.totalPages}
                </span>
                <button
                  type="button"
                  className={styles.pageBtn}
                  disabled={page >= pagination.totalPages || pageLoading}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Suivant →
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <ConfirmModal
        open={!!confirmModal}
        variant={confirmModal?.variant || 'warning'}
        title={confirmModal?.title}
        message={confirmModal?.message}
        detail={confirmModal?.detail}
        confirmLabel={confirmModal?.confirmLabel || 'Supprimer'}
        onConfirm={handleConfirmModal}
        onCancel={() => setConfirmModal(null)}
        loading={deleting}
      />
    </div>
  )
}

export default CorporateDashboard
